import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import { invalidatePlanningOverride } from './invalidateForecastViews';
import type {
  LeadTimeOverrideRow,
  ListLeadTimeOverridesResponse,
} from '@erp/rpc';

export type { LeadTimeOverrideRow, ListLeadTimeOverridesResponse };

export interface UseLeadTimeOverridesInput {
  /** Null disables the query (rows without a warehouse have no series). */
  warehouseId: string | null;
  /** ProductV3 key (the SKU); null disables the query too. */
  productV3Key?: string | null;
}

// Every lead-time override (active + history) for one SKU x warehouse, plus
// which one applies right now. ProductV3 only: there is no legacy v2 lookup.
export function useLeadTimeOverrides(
  input: UseLeadTimeOverridesInput,
  enabled = true,
) {
  return useQuery<ListLeadTimeOverridesResponse>({
    ...orpc.procurement.leadTimeOverrides.list.queryOptions({
      input: {
        // Never sent when null; `enabled` gates the fetch below.
        warehouseId: input.warehouseId ?? '',
        productV3Key: input.productV3Key ?? '',
      },
    }),
    enabled: enabled && !!input.warehouseId && !!input.productV3Key,
  });
}

export function useCreateLeadTimeOverride() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.procurement.leadTimeOverrides.create.mutationOptions({
      onSuccess: () => invalidatePlanningOverride(queryClient, 'leadTime'),
    }),
  );
}

export function useUpdateLeadTimeOverride() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.procurement.leadTimeOverrides.update.mutationOptions({
      onSuccess: () => invalidatePlanningOverride(queryClient, 'leadTime'),
    }),
  );
}

export function useRevokeLeadTimeOverride() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.procurement.leadTimeOverrides.revoke.mutationOptions({
      onSuccess: () => invalidatePlanningOverride(queryClient, 'leadTime'),
    }),
  );
}
